"use client";

import * as React from "react";
import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { Input } from "./ui/input";
import { Search, SlidersHorizontal, Calendar, X } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { Popover, PopoverContent, PopoverAnchor } from "./ui/popover";
import { cn } from "../lib/utils";

type FilterKind = "all" | "agent" | "insurance" | "date";

type Assistant = {
  id: string;
  name?: string;
  agent_id?: number;
  companies?: Array<{ id: number | string; name?: string }>;
};

type DateFilter = {
  mode: "single" | "range";
  from?: string;
  to?: string;
};

interface SidebarFiltersProps {
  searchQuery: string;
  sortOrder: "asc" | "desc";
  onSearchQueryChange: (value: string) => void;
  onSortOrderChange: (value: "asc" | "desc") => void;
  filterKind: FilterKind;
  onFilterKindChange: (value: FilterKind) => void;
  assistants: Assistant[];
  companyId: number | string;
  selectedAgentId: string | null;
  onAgentSelect: (assistantId: string | null) => void;
  dateFilter: DateFilter;
  onDateFilterChange: (next: DateFilter) => void;
  t: (key: string) => string;
  isHistoryPage?: boolean;
}

export const SidebarFilters: React.FC<SidebarFiltersProps> = ({
  searchQuery,
  sortOrder,
  onSearchQueryChange,
  onSortOrderChange,
  filterKind,
  onFilterKindChange,
  assistants,
  companyId,
  selectedAgentId,
  onAgentSelect,
  dateFilter,
  onDateFilterChange,
  t,
}) => {
  const [dateOpen, setDateOpen] = useState(false);
  const [localMode, setLocalMode] = useState<"single" | "range">(dateFilter.mode || "single");
  const [localFrom, setLocalFrom] = useState<string | undefined>(dateFilter.from);
  const [localTo, setLocalTo] = useState<string | undefined>(dateFilter.to);
  const fromInputRef = useRef<HTMLInputElement | null>(null);
  const toInputRef = useRef<HTMLInputElement | null>(null);
  const ignoreCloseRef = useRef(false);

  const maxDate = useMemo(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }, []);

  useEffect(() => {
    setLocalMode(dateFilter.mode || "single");
    setLocalFrom(dateFilter.from);
    setLocalTo(dateFilter.to);
  }, [dateFilter]);

  const openDatePopoverSafely = () => {
    ignoreCloseRef.current = true;
    setDateOpen(true);
    setTimeout(() => {
      ignoreCloseRef.current = false;
    }, 100);
  };

  const clearDate = () => {
    setLocalFrom(undefined);
    setLocalTo(undefined);
    onDateFilterChange({ mode: "single" });
    onFilterKindChange("all");
  };

  const applyDateFilter = () => {
    if (localMode === "single" && localFrom) {
      onDateFilterChange({ 
        mode: "single", 
        from: localFrom, 
        to: localFrom 
      });
    } else if (localMode === "range" && localFrom && localTo) {
      onDateFilterChange({ 
        mode: "range", 
        from: localFrom, 
        to: localTo 
      });
    } else if (localMode === "range" && localFrom && !localTo) {
      onDateFilterChange({ 
        mode: "range", 
        from: localFrom
      });
    }
    setDateOpen(false);
  };

  const companyAgents = useMemo(() => {
    if (!assistants || !companyId) return [];
    return assistants.filter((a: any) =>
      Array.isArray(a.companies) && a.companies.some((c: any) => c.id === companyId)
    );
  }, [assistants, companyId]);

  const renderAgentLabel = (agent: any) => {
    if (!agent) return "";
    return agent.name || `Agent ${agent.id}`;
  };

  return (
    <div className="relative flex items-center gap-2 p-2">
      <div className="flex items-center border rounded-full text-[#4A5364] px-2 w-full">
        <Search size={20} className="text-[#4A5364]" />
        <Input
          placeholder={t("search")}
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          className="w-full rounded-full border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>

      {filterKind === "date" ? (
        <div className="flex items-center gap-1">
          <button
            onClick={openDatePopoverSafely}
            className="h-8 w-8 p-0 flex items-center justify-center border-none text-[#4A5364] hover:bg-[#F6F7F9] rounded"
            aria-label={t("filter")}
          >
            <SlidersHorizontal size={20} />
          </button>
          <button
            onClick={() => {
              onFilterKindChange("all");
              clearDate();
            }}
            className="h-6 w-6 p-0 flex items-center justify-center border-none text-[#6F7D95] hover:bg-[#F6F7F9] rounded-full"
            aria-label={t("clear_filter")}
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <Select
          value={filterKind}
          onValueChange={(v: FilterKind) => {
            onFilterKindChange(v);
            if (v === "date") {
              openDatePopoverSafely();
            } else {
              setDateOpen(false);
              if (v === "all") {
                clearDate();
              }
            }
          }}
        >
          <SelectTrigger
            aria-label={t("filter")}
            hideIcon
            className="h-8 w-8 p-0 flex items-center justify-center border-none text-[#4A5364] hover:bg-[#F6F7F9] no-focus-ring"
          >
            <SelectValue>
              <span className="sr-only">
                {{
                  all: t("all"),
                  agent: t("agent"),
                  insurance: t("insurance"),
                  date: t("date"),
                }[filterKind]}
              </span>
            </SelectValue>
            <SlidersHorizontal size={20} className="pointer-events-none" />
          </SelectTrigger>

          <SelectContent className="min-w-[180px]">
            <SelectItem value="all">{t("all")}</SelectItem>
            <SelectItem value="date">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                {t("date")}
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      )}

      {filterKind === "agent" && (
        <Select
          value={selectedAgentId ?? "all"}
          onValueChange={(v) => onAgentSelect(v === "all" ? null : v)}
        >
          <SelectTrigger className="h-9 min-w-[180px] no-focus-ring">
            <SelectValue>
              {selectedAgentId
                ? renderAgentLabel(companyAgents.find((a) => a.id === selectedAgentId))
                : t("all_agents")}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="min-w-[220px]">
            <SelectItem value="all">{t("all_agents")}</SelectItem>
            {companyAgents.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {renderAgentLabel(a)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Popover
        open={dateOpen}
        onOpenChange={(next) => {
          if (!next && ignoreCloseRef.current) return;
          setDateOpen(next);
        }}
      >
        <PopoverAnchor asChild>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0" />
        </PopoverAnchor>

        <PopoverContent
          side="right"
          align="start"
          sideOffset={12}
          className="w-[320px] p-3 bg-white rounded-xl shadow-xl border border-[#E2E4E9]"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            if (ignoreCloseRef.current) e.preventDefault();
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Select
              value={localMode}
              onValueChange={(v: "single" | "range") => setLocalMode(v)}
            >
              <SelectTrigger className="h-9 no-focus-ring">
                <SelectValue>
                  {localMode === "single" ? t("single_date") : t("date_range")}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">{t("single_date")}</SelectItem>
                <SelectItem value="range">{t("date_range")}</SelectItem>
              </SelectContent>
            </Select>

            <button
              type="button"
              className="ml-auto text-xs text-[#6F7D95] hover:underline"
              onClick={clearDate}
            >
              {t("clear")}
            </button>
          </div>

          <div className={cn("grid gap-2", localMode === "range" ? "grid-cols-2" : "grid-cols-1")}>
            <div className="flex flex-col">
              <label className="text-xs text-[#6F7D95] mb-1">{t("from")}</label>
              <div className="relative">
                <input
                  ref={(el) => (fromInputRef.current = el)}
                  type="date"
                  max={maxDate}
                  className="h-9 rounded-md border px-2 pr-8 text-sm bg-white w-full"
                  value={localFrom ?? ""}
                  onChange={(e) => setLocalFrom(e.target.value || undefined)}
                />
                <Calendar 
                  size={16} 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6F7D95] cursor-pointer" 
                  onClick={() => fromInputRef.current?.showPicker?.()}
                />
              </div>
            </div>

            {localMode === "range" && (
              <div className="flex flex-col">
                <label className="text-xs text-[#6F7D95] mb-1">{t("to")}</label>
                <div className="relative">
                  <input
                    ref={(el) => (toInputRef.current = el)}
                    type="date"
                    max={maxDate}
                    min={localFrom}
                    className="h-9 rounded-md border px-2 pr-8 text-sm bg-white w-full"
                    value={localTo ?? ""}
                    onChange={(e) => setLocalTo(e.target.value || undefined)}
                  />
                  <Calendar 
                    size={16} 
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6F7D95] cursor-pointer" 
                    onClick={() => toInputRef.current?.showPicker?.()}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              className="text-xs text-[#6F7D95] hover:underline px-3 py-1"
              onClick={() => setDateOpen(false)}
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              className="text-xs bg-[#105BDB] text-white px-3 py-1 rounded-md hover:bg-[#0055C4] disabled:bg-gray-300 disabled:cursor-not-allowed"
              onClick={applyDateFilter}
              disabled={!localFrom}
            >
              {t("apply")}
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
